export type VillageTravelIntent = {
  readonly sequence: number;
  readonly signal: AbortSignal;
  isCurrent(): boolean;
  finish(): void;
};

export class VillageTravelSupersededError extends Error {
  override readonly name = 'VillageTravelSupersededError';

  constructor() {
    super('A newer village travel request replaced this one.');
  }
}

export function isVillageTravelSuperseded(error: unknown): boolean {
  return error instanceof VillageTravelSupersededError;
}

type ActiveVillageTravel = {
  readonly controller: AbortController;
  readonly detachSourceAbort: () => void;
};

export class VillageTravelCoordinator {
  private active: ActiveVillageTravel | undefined;

  constructor(
    private sequence = 0,
    private readonly persistSequence: (sequence: number) => void = () => undefined,
  ) {
    if (!Number.isSafeInteger(sequence) || sequence < 0) {
      throw new Error('Village-travel sequence must be a non-negative safe integer');
    }
  }

  get lastSequence(): number {
    return this.sequence;
  }

  begin(sourceSignal: AbortSignal): VillageTravelIntent {
    this.cancel();
    const controller = new AbortController();
    const abortFromSource = () => controller.abort(sourceSignal.reason);
    if (sourceSignal.aborted) {
      abortFromSource();
    } else {
      sourceSignal.addEventListener('abort', abortFromSource, { once: true });
    }
    const active: ActiveVillageTravel = {
      controller,
      detachSourceAbort: () => sourceSignal.removeEventListener('abort', abortFromSource),
    };
    this.active = active;
    const sequence = ++this.sequence;
    this.persistSequence(sequence);
    return {
      sequence,
      signal: controller.signal,
      isCurrent: () => this.active === active && !controller.signal.aborted,
      finish: () => {
        active.detachSourceAbort();
        if (this.active === active) this.active = undefined;
      },
    };
  }

  cancel(): void {
    const active = this.active;
    if (active === undefined) return;
    this.active = undefined;
    active.detachSourceAbort();
    active.controller.abort();
  }
}
